import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import useTokenData from '@/hooks/useTokenData';
import type { TokenConfigType, TokenDataAllType } from '@/constants/types/currency';
import type { Address } from 'viem';
import type { ChainType } from '@/constants/chain-specific/chains';

/**
 * Shared token balance cache system
 * Provides a centralized way to cache and share token balance data across components
 */

export interface SharedTokenBalanceData {
  tokenData: Record<Address, TokenDataAllType>;
  isLoading: boolean;
  error: Error | null;
  refresh: () => void;
}

/**
 * Hook for managing shared token balances with caching
 * This replaces individual useTokenData calls with a centralized cache
 */
export function useSharedTokenBalances(
  tokens: TokenConfigType[],
  account?: Address,
  spender?: Address,
  chainId?: ChainType,
  options?: {
    enabled?: boolean;
    staleTime?: number;
  }
): SharedTokenBalanceData {
  const { enabled = true, staleTime = 1000 * 60 * 2 } = options || {}; // 2 minutes default

  // Create a stable key for caching
  const queryKey = [
    'shared-token-balances',
    chainId,
    account,
    spender,
    tokens.map(t => t.address).sort().join(',')
  ];

  // Use the existing useTokenData hook directly
  const { tokenData, refresh: refreshTokenData, isLoading } = useTokenData(
    tokens,
    account,
    spender,
    chainId as number
  );

  const queryClient = useQueryClient();

  // Cache the data when it changes
  useEffect(() => {
    if (Object.keys(tokenData).length > 0) {
      queryClient.setQueryData(queryKey, tokenData);
    }
  }, [tokenData, queryKey, queryClient]);

  const refresh = useCallback(() => {
    refreshTokenData();
    // Also invalidate the query cache
    queryClient.invalidateQueries({ queryKey });
  }, [refreshTokenData, queryKey, queryClient]);

  return {
    tokenData: tokenData,
    isLoading: isLoading,
    error: null,
    refresh,
  };
}

/**
 * Hook to get cached token balances without triggering a new fetch
 * Useful for components that want to display cached data only
 */
export function useCachedTokenBalances(
  chainId?: ChainType,
  account?: Address,
  spender?: Address
): Record<Address, TokenDataAllType> | undefined {
  const queryClient = useQueryClient();

  // Try to get any cached token balance data for this account/chain combination
  const cacheKeys = queryClient.getQueryCache().getAll()
    .filter(query => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === 'shared-token-balances' &&
        key[1] === chainId &&
        key[2] === account &&
        key[3] === spender
      );
    });

  // Return the most recent cached data
  if (cacheKeys.length > 0) {
    const mostRecent = cacheKeys
      .sort((a, b) => (b.state.dataUpdatedAt || 0) - (a.state.dataUpdatedAt || 0))[0];
    return mostRecent.state.data as Record<Address, TokenDataAllType>;
  }

  return undefined;
}

/**
 * Preload token balances for better UX
 * Call this when you know tokens will be needed soon
 */
export function preloadTokenBalances(
  queryClient: any,
  tokens: TokenConfigType[],
  account: Address,
  chainId: ChainType,
  spender?: Address
) {
  const queryKey = [
    'shared-token-balances',
    chainId,
    account,
    spender,
    tokens.map(t => t.address).sort().join(',')
  ];

  queryClient.prefetchQuery({
    queryKey,
    queryFn: async () => {
      // This will be handled by the component that actually uses useTokenData
      return {};
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Get token balance for a specific token from cache
 */
export function useTokenBalance(
  tokenAddress: Address,
  chainId?: ChainType,
  account?: Address
): TokenDataAllType | undefined {
  const cachedData = useCachedTokenBalances(chainId, account);
  return cachedData?.[tokenAddress];
}

/**
 * Invalidate all token balance caches for a specific account/chain
 * Useful after transactions that might change balances
 */
export function useInvalidateTokenBalances() {
  const queryClient = useQueryClient();

  return useCallback((chainId?: ChainType, account?: Address) => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        if (!Array.isArray(key) || key[0] !== 'shared-token-balances') {
          return false;
        }
        
        // Invalidate all queries for this chain/account combination
        return (!chainId || key[1] === chainId) && (!account || key[2] === account);
      }
    });
  }, [queryClient]);
}
